import { Fragment, useState } from "react"
import { Affix, Button, Divider, Input, Modal, Spin } from "antd"
import { CheckOutlined, SaveOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router"
import { Loading, Report } from "notiflix"

import Navbar from "../../atoms/navbar"
import Footer from "../../atoms/footer"
import ImportantInfo from "../../atoms/important-updates"

import "./styles.css"

import { useQuestionnaireSubmissionStore } from "@/stores/questionnaire-submissions/questionnaireSubmissionStore"
import { IQuestionnaireSubmission, IQuestionnaireSubmissionDto, STRESS_CATEGORY_LABEL, StressCategory } from "@/types"
import { DEFAULT_COMPANY, QUESTIONNAIRE_ITEMS, QUESTIONNAIRE_SCALE } from "./constant"

const categoryColorMap: Record<StressCategory, string> = {
  [StressCategory.NORMAL]: "#52c41a",
  [StressCategory.MILD]: "#faad14",
  [StressCategory.MODERATE]: "#fa8c16",
  [StressCategory.SEVERE]: "#f5222d",
}

type SigninCompany = { _id?: string; code?: string; fullname?: string }
type SigninSession = { _id?: string; nik?: string; fullname?: string; email?: string; company?: SigninCompany }
type FarmsSession = {
  _id?: string
  id?: string
  object_id?: string
  employee_id?: string
  fullname?: string
  business_entity?: string
  branch_nickname?: string
  branch_id?: string
  company_id?: string | number
  mobile_phone?: string
  birth_date?: string
}

const readJSON = <T,>(key: string): T | null => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const calculateAge = (birthDate?: string): number | null => {
  if (!birthDate) return null
  const birth = new Date(birthDate)
  if (Number.isNaN(birth.getTime())) return null
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }
  return age >= 0 ? age : null
}

const { TextArea } = Input

const scaleDotColor: Record<number, string> = {
  0: "#52c41a",
  1: "#faad14",
  2: "#fa8c16",
  3: "#f5222d",
}

const scaleBgColor: Record<number, string> = {
  0: "#f6ffed",
  1: "#fffbe6",
  2: "#fff7e6",
  3: "#fff1f0",
}

const scaleShadowColor: Record<number, string> = {
  0: "rgba(82, 196, 26, 0.25)",
  1: "rgba(250, 173, 20, 0.25)",
  2: "rgba(250, 140, 22, 0.25)",
  3: "rgba(245, 34, 45, 0.25)",
}

type AnswerMap = Record<string, number | null>

const buildInitialAnswers = (): AnswerMap => {
  const map: AnswerMap = {}
  QUESTIONNAIRE_ITEMS.forEach(item => {
    map[item.key] = null
  })
  return map
}

const WellnessAdd = () => {
  const navigate = useNavigate()
  const { createQuestionnaireSubmission } = useQuestionnaireSubmissionStore()

  const [session] = useState<FarmsSession>(() => readJSON<FarmsSession>("session_farms") || {})
  const [signin] = useState<SigninSession>(() => readJSON<SigninSession>("session_signin") || {})

  const [submitting, setSubmitting] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState<string>(() => {
    return ""
  })
  const [note, setNote] = useState<string>("")
  const [answers, setAnswers] = useState<AnswerMap>(buildInitialAnswers())

  const computedAge = calculateAge(session.birth_date)
  const displayFullname = session.fullname || signin.fullname || "-"
  const displayEmployeeId = session.employee_id || signin.nik || "-"
  const displayAge = computedAge !== null ? String(computedAge) : "-"
  const displayCompanyCode = signin.company?.code || session.business_entity || DEFAULT_COMPANY.code
  const displayCompanyName = signin.company?.fullname || DEFAULT_COMPANY.name

  const handleAnswerChange = (key: string, value: number) => {
    setAnswers(prev => ({ ...prev, [key]: value }))
  }

  const showResultModal = (submission: IQuestionnaireSubmission) => {
    const category = submission.category
    const color = categoryColorMap[category] || "#1890ff"
    const categoryLabel = STRESS_CATEGORY_LABEL[category] || category
    const needsFollowup = submission.needs_followup

    Modal.success({
      title: "Jawaban Berhasil Dikirim",
      centered: true,
      width: 520,
      okText: "Kembali ke Beranda",
      onOk: () => navigate("/home"),
      icon: null,
      content: (
        <div className="wellness-result-modal">
          <p className="wellness-result-intro">Terima kasih, jawaban Anda telah kami terima. Berikut hasil ringkasan kuesioner Anda:</p>

          <div className="wellness-result-row">
            <div className="wellness-result-score">
              <div className="wellness-result-score-value" style={{ color }}>
                {submission.total_score}
              </div>
              <div className="wellness-result-score-label">Total Skor</div>
            </div>
            <div className="wellness-result-category" style={{ borderColor: color }}>
              <div className="wellness-result-category-tag" style={{ background: color }}>
                {category}
              </div>
              <div className="wellness-result-category-desc">{categoryLabel}</div>
            </div>
          </div>

          {needsFollowup && (
            <div className="wellness-result-followup">
              <strong>Perlu Tindak Lanjut</strong>
              <p>Berdasarkan hasil kuesioner Anda, tim HR akan menghubungi Anda untuk diarahkan ke psikolog guna mendapatkan pendampingan lebih lanjut. Mohon pastikan nomor WhatsApp yang Anda isi dapat dihubungi.</p>
            </div>
          )}
        </div>
      ),
    })
  }

  const validateForm = (): boolean => {
    if (computedAge === null) {
      Report.warning("Validasi Data", "Tanggal lahir tidak ditemukan di session. Hubungi HR untuk melengkapi data.", "Mengerti")
      return false
    }

    if (!phoneNumber.trim()) {
      Report.warning("Validasi Data", "Mohon isi nomor WhatsApp Anda.", "Mengerti")
      return false
    }

    const missing = QUESTIONNAIRE_ITEMS.some(item => answers[item.key] === null || answers[item.key] === undefined)
    if (missing) {
      Report.warning("Validasi Data", "Mohon jawab seluruh pertanyaan kuesioner.", "Mengerti")
      return false
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setSubmitting(true)
      Loading.dots("Mengirim jawaban...")

      const payload: IQuestionnaireSubmissionDto = {
        user_id: session._id || session.id || session.object_id || signin._id || "",
        fullname: displayFullname !== "-" ? displayFullname : "",
        age: computedAge ?? 0,
        phone_number: phoneNumber.trim(),
        company_id: session.branch_id || (session.branch_id ? String(session.branch_id) : ""),
        company_code: displayCompanyCode,
        company_name: displayCompanyName,
        note: note.trim(),
        item_1: answers.item_1,
        item_2: answers.item_2,
        item_3: answers.item_3,
        item_4: answers.item_4,
        item_5: answers.item_5,
        item_6: answers.item_6,
        item_7: answers.item_7,
        item_8: answers.item_8,
        item_9: answers.item_9,
        item_10: answers.item_10,
        item_11: answers.item_11,
        item_12: answers.item_12,
        item_13: answers.item_13,
        item_14: answers.item_14,
      }

      const submission = await createQuestionnaireSubmission(payload)
      Loading.remove()
      showResultModal(submission)
    } catch (error) {
      const errorMessage = error instanceof Error && "response" in error ? (error as { response?: { data?: { message?: string } } }).response?.data?.message || "Gagal mengirim jawaban." : "Gagal mengirim jawaban."
      Report.warning("Oops.. ada kendala", errorMessage, "Mengerti")
    } finally {
      setSubmitting(false)
      Loading.remove()
    }
  }

  return (
    <Fragment>
      <Navbar title="Create" back subtitle="Wellness Support Guide" navigate="/home" />

      <div className="container white-back-cr" style={{ minHeight: 500 }}>
        <ImportantInfo />

        <div className="container mt-3 p-3">
          <div className="wellness-card">
            <p className="wellness-section-title mb-1">Informasi Karyawan</p>
            <small className="text-muted">Data otomatis dari Talenta, hubungi HR jika ada ketidaksesuaian.</small>
            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <label className="wellness-field-label">Nama Lengkap</label>
                <Input value={displayFullname} disabled />
              </div>
              <div className="col-md-6">
                <label className="wellness-field-label">Employee ID</label>
                <Input value={displayEmployeeId} disabled />
              </div>
              <div className="col-md-6">
                <label className="wellness-field-label">
                  Nomor Telepon / WhatsApp <span className="text-danger">*</span>
                </label>
                <Input value={phoneNumber} onChange={e => setPhoneNumber(e.target.value)} placeholder="Contoh: 08123456789" />
              </div>
              <div className="col-md-3">
                <label className="wellness-field-label">Usia</label>
                <Input value={displayAge !== "-" ? `${displayAge} tahun` : "-"} disabled />
              </div>
              <div className="col-md-3">
                <label className="wellness-field-label">Kode Branch</label>
                <Input value={displayCompanyCode} disabled />
              </div>
              <div className="col-md-6">
                <label className="wellness-field-label">Nama Branch</label>
                <Input value={displayCompanyName} disabled />
              </div>
            </div>
          </div>

          <Divider orientation="left">Kuesioner</Divider>

          <div className="wellness-card">
            <div className="wellness-info-box">
              <div className="wellness-info-title">Petunjuk Pengisian:</div>
              <p className="wellness-info-paragraph">
                Berikut ini adalah sejumlah pernyataan yang berkaitan dengan kondisi yang mungkin Anda alami dalam <strong>satu minggu terakhir</strong>. Silakan baca setiap pernyataan dengan saksama, kemudian pilih jawaban yang paling
                menggambarkan pengalaman Anda selama periode tersebut. Tidak ada jawaban benar atau salah. Jawablah secara jujur sesuai dengan kondisi yang Anda rasakan. Terdapat empat pilihan jawaban yang disediakan untuk setiap
                pernyataan.
              </p>
              <ul className="wellness-scale-list">
                {QUESTIONNAIRE_SCALE.map(option => (
                  <li key={option.value}>
                    <span className="wellness-scale-list-badge" style={{ background: scaleDotColor[option.value] }}>
                      {option.value}
                    </span>
                    <span className="wellness-scale-list-text">{option.label}.</span>
                  </li>
                ))}
              </ul>
            </div>

            {submitting ? (
              <div className="text-center my-5">
                <Spin size="large" />
              </div>
            ) : (
              QUESTIONNAIRE_ITEMS.map((item, index) => (
                <div key={item.key} className="wellness-item">
                  <div className="wellness-item-header">
                    <span className="wellness-item-number">{index + 1}</span>
                    <span className="wellness-item-text">{item.label}</span>
                  </div>
                  <div className="wellness-options" role="radiogroup" aria-label={item.label}>
                    {QUESTIONNAIRE_SCALE.map(option => {
                      const isSelected = answers[item.key] === option.value
                      const styleVars = {
                        "--option-color": scaleDotColor[option.value],
                        "--option-bg": scaleBgColor[option.value],
                        "--option-shadow": scaleShadowColor[option.value],
                      } as React.CSSProperties
                      return (
                        <div
                          key={option.value}
                          role="radio"
                          aria-checked={isSelected}
                          tabIndex={0}
                          className={`wellness-option${isSelected ? " is-selected" : ""}`}
                          style={styleVars}
                          onClick={() => handleAnswerChange(item.key, option.value)}
                          onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault()
                              handleAnswerChange(item.key, option.value)
                            }
                          }}
                        >
                          <span className="wellness-option-check">
                            <CheckOutlined />
                          </span>
                          <span className="wellness-option-badge">{option.value}</span>
                          <span className="wellness-option-label">{option.label}</span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="wellness-card">
            <p className="wellness-section-title mb-1">
              Catatan Tambahan <span className="wellness-optional-badge">Opsional</span>
            </p>
            <small className="text-muted">Kolom ini bersifat opsional. Silakan ceritakan keluhan atau hal yang ingin Anda sampaikan.</small>
            <div className="row g-3 mt-2">
              <div className="col-12">
                <TextArea rows={4} placeholder="Contoh: Saya sedang merasa kurang fokus beberapa minggu terakhir..." value={note} onChange={e => setNote(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Affix offsetBottom={0}>
        <div className="wellness-submit-bar text-center">
          <Button type="primary" icon={<SaveOutlined />} size="large" loading={submitting} onClick={handleSubmit} className="wellness-submit-btn">
            Kirim Jawaban
          </Button>
        </div>
      </Affix>
      <Footer />
    </Fragment>
  )
}

export default WellnessAdd
